import React from 'react';
import Chip from '../shared/Chip';
import Icon from '../shared/Icon';
import { OUTCOMES, clock } from '../../../models/assessmentEvaluation';

const TITLE = 'Comparison against reference key';

/*
 * The outcome mark, drawn as the Checkbox: a check for Found, the
 * indeterminate dash for Unclear, an empty box for Not found. A readout, not a
 * control: no input, not focusable, hidden from assistive technology (the card
 * says its outcome in words), and at full opacity.
 */
function OutcomeBox({ outcome }) {
  const { mark } = OUTCOMES[outcome] || OUTCOMES.not_found;
  return (
    <span className="assessment-check-box" aria-hidden="true">
      {mark && <Icon name={mark} size={14} />}
    </span>
  );
}

const activate = (fn) => (e) => {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  e.preventDefault();
  fn();
};

/*
 * One reference key item (Assessment Comparison Card, 1208:11591). The card is
 * the control, not the chip inside it: anywhere on it goes to the evidence.
 * One with no evidence is not a control at all. Found carries the quote alone;
 * Unclear and Not found name themselves in front of it, so no outcome rests on
 * the mark alone.
 */
function ComparisonCard({ item, fromTranscript, onSeek, onShowPassage }) {
  const { label } = OUTCOMES[item.outcome];
  const quote = item.segment && item.segment.text;
  const evidence = item.segment
    ? {
      go: fromTranscript ? () => onShowPassage(item.segIndex) : () => onSeek(item.segment.at),
      name: fromTranscript
        ? `${item.text}. ${label}. Show in transcript`
        : `${item.text}. ${label} at ${clock(item.segment.at)}. Play from there`,
    }
    : null;
  const classes = [
    'assessment-eval-card',
    item.outcome === 'found' ? '' : 'assessment-eval-card--quiet',
    evidence ? 'is-clickable' : '',
  ].filter(Boolean).join(' ');
  const control = evidence
    ? { role: 'button', tabIndex: 0, 'aria-label': evidence.name, onClick: evidence.go, onKeyDown: activate(evidence.go) }
    : {};

  return (
    <div data-component="Assessment Comparison Card" className={classes} {...control}>
      <OutcomeBox outcome={item.outcome} />
      <div className="assessment-eval-body">
        <div className="assessment-eval-item">{item.text}</div>
        <div className="assessment-eval-evidence">
          {item.outcome === 'found'
            ? <span className="library-sr-only">Found.</span>
            : <span className="assessment-eval-state">{quote ? `${label} · ` : label}</span>}
          {quote && <span className="assessment-eval-quote">{quote}</span>}
        </div>
      </div>
      {item.segment && !fromTranscript && (
        <span className="assessment-eval-time" aria-hidden="true">{clock(item.segment.at)}</span>
      )}
    </div>
  );
}

/**
 * The left column on a Completed assessment: one card per reference key item,
 * grouped by the key's sections in key order, under a count chip. Nothing
 * reads as a pass or a fail, and there is no score: accuracy is not evaluated
 * in this phase.
 */
export function AssessmentComparison({ groups, counts, fromTranscript, onSeek, onShowPassage }) {
  return (
    <>
      <h3 className="assessment-eval-head">{TITLE}</h3>
      <p className="assessment-eval-count">
        <Chip>{`${counts.found} of ${counts.total} items`}</Chip>
      </p>
      <div className="assessment-eval-list" role="region" aria-label={TITLE}>
        {groups.map((group) => (
          <div key={group.section} className="assessment-eval-group">
            <div className="assessment-eval-group-head">{group.section}</div>
            {group.items.map((item) => (
              <ComparisonCard
                key={item.id}
                item={item}
                fromTranscript={fromTranscript}
                onSeek={onSeek}
                onShowPassage={onShowPassage}
              />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * The left column while Processing: the key with no outcome against it, so the
 * evaluator can see what is being compared while it runs.
 */
export function ReferenceKeyList({ sections, keyName }) {
  return (
    <>
      <h3 className="assessment-eval-head">{TITLE}</h3>
      <p className="assessment-eval-count">{keyName || 'Reference key'}</p>
      <div className="assessment-eval-list" role="region" aria-label="Reference key">
        {sections.map((section) => (
          <div key={section.section} className="assessment-eval-group">
            <div className="assessment-eval-group-head">{section.section}</div>
            {section.items.map((item) => (
              <div key={item.id} className="assessment-eval-card assessment-eval-card--waiting">
                <OutcomeBox outcome="not_found" />
                <div className="assessment-eval-body">
                  <div className="assessment-eval-item">{item.text}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}
